import React, { useMemo, useState } from 'react'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import ErrorList from 'src/components/ErrorList/ErrorList'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
// import LoadingShimmer from 'src/components/LoadingShimmer/LoadingShimmer'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import Trns from 'src/localization/Trns'
import styled, { css } from 'styled-components'

export interface GenericLayoutDetailsComponentProps<
  T extends Record<keyof T, string>,
> {
  /* rendered when not updating */
  staticContent: React.ReactElement
  /* rendered when updating, if not undefined an update button will be rendered by the component */
  updatingContent?: React.ReactElement
  /* shows regardless of updating state */
  additionalContent?: React.ReactElement
  saveLoading?: boolean
  onUpdateToggle?: (updating: boolean) => void
  onCancel?: () => void
  onSave?: (e?: React.FormEvent) => boolean | Promise<boolean>
  updating?: boolean
  error?: string
  disallowUpdate?: boolean
}

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`

const FlexRow = styled.div`
  display: flex;
`

const GenericLayoutDetailsComponent = <T extends Record<keyof T, string>>(
  props: GenericLayoutDetailsComponentProps<T>,
): React.ReactElement => {
  const [updating, setUpdating] = useState<boolean>(false)

  const showUpdateButton = useMemo(
    () =>
      !props.disallowUpdate && props.updatingContent !== undefined && !updating,
    [props.disallowUpdate, props.updatingContent, updating],
  )

  const onSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault()

    if (props.onSave === undefined) {
      setUpdating(false)
    } else if (await props.onSave()) {
      setUpdating(false)
    }
  }

  const onCancelClick = (): void => {
    setUpdating(false)
    props.onCancel?.()
  }

  return (
    <FlexColumn
      css={css`
        gap: 20px;
      `}
    >
      <form
        onSubmit={onSubmit}
        css={css`
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 20px;
        `}
      >
        {showUpdateButton && (
          <HoverableLink
            css={css`
              height: 18px;
              display: flex;
              align-items: center;
              position: absolute;
              top: 0;
              right: 0;
            `}
            ariaLabelSource={'Update'}
            testId={'genericLayoutDetailsUpdateBtn'}
            onClick={(): void => setUpdating(true)}
          >
            <Trns source={'Update'} />
            <SvgUpdate
              css={css`
                transform: scale(calc(14 / 24));
              `}
            />
          </HoverableLink>
        )}
        {updating ? props.updatingContent : props.staticContent}
        {updating && (
          <FlexRow
            css={css`
              gap: 40px;
            `}
          >
            <Button
              big
              testId={'saveChanges'}
              design={ButtonDesigns.designCta}
              type={ButtonTypes.submit}
              text={<Trns source={'Save Changes'} />}
              loading={props.saveLoading}
            />
            <HoverableLink
              testId={'cancel'}
              onClick={onCancelClick}
              ariaLabelSource={'Cancel'}
            >
              <Trns source={'Cancel'} />
            </HoverableLink>
          </FlexRow>
        )}
      </form>
      {props.additionalContent && (
        <>
          <DividerLineDiv
            css={css`
              margin: 0;
            `}
          />
          {props.additionalContent}
        </>
      )}
      {props.error !== undefined && (
        <ErrorList
          errors={[props.error]}
          css={css`
            margin-top: 10px;
            margin-bottom: 10px;
          `}
        />
      )}
    </FlexColumn>
  )
}

export default React.memo(GenericLayoutDetailsComponent)
